import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HorizontalContainer,
  HorizontalResizableSectionContainer,
  HorizontalResizer,
} from './atoms';
import { getMouseDownHandler } from './getMouseDownHandler';
import {
  Direction,
  ResizableHorizontalChildren,
  ResizeMouseEventHandler,
  State,
} from './types';
import { reduceIsCollapsed } from './reduceIsCollapsed';
import {
  getContainerState,
  getHorizontalConfigFromChild,
  haveChildrenDimensionsConfigPropsChangedHorizontal,
  toInitialStyle,
  updateConfigs,
} from './utils';
import { reduceIsCollapsedAllChildren } from './reduceIsCollapsedAllChildren';

type ResizableHorizontalProps = {
  children: ResizableHorizontalChildren;
  isPropagate?: boolean;
  onResizeEnd?: ResizeMouseEventHandler;
  onResizeStart?: ResizeMouseEventHandler;
  hasHorizontalScroll?: boolean;
};

const ResizableHorizontal = ({
  children,
  isPropagate = true,
  onResizeEnd,
  onResizeStart,
  hasHorizontalScroll = false,
}: ResizableHorizontalProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [
    {
      activeResizerIndex,
      styles,
      configs,
      resizerStyles,
      isResizeEndScheduled,
    },
    setState,
  ] = useState<State>(() => ({
    startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
    configs: children.map(getHorizontalConfigFromChild),
    activeResizerIndex: -1,
    styles: children.map(toInitialStyle),
    resizerStyles: [],
    isPropagate,
    direction: Direction.HORIZONTAL,
    isStyleInitialized: false,
    isResizeEndScheduled: false,
  }));

  useEffect(() => {
    if (haveChildrenDimensionsConfigPropsChangedHorizontal(children, configs)) {
      setState(previousState => ({
        ...previousState,
        startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
        configs: children.map(getHorizontalConfigFromChild),
        activeResizerIndex: -1,
        styles: children.map(toInitialStyle),
        resizerStyles: [],
      }));
    }
  }, [children, configs]);

  useEffect(() => {
    if (
      containerRef.current &&
      // Check if the component is rendered and has a visible size.
      // This is crucial because if an ancestor has 'display: none',
      // getClientRects() would return an empty list, leading to incorrect
      // calculations.
      containerRef.current.getClientRects().length > 0
    ) {
      // This may seem to called a bit often, but
      // reduceIsCollapsedAllChildren can return the current state,
      // so not each call will trigger a render update.
      // The advantage of doing it in this way is that internal state
      // can be accessed.
      setState(state =>
        reduceIsCollapsedAllChildren(state, {
          children,
          containerRef,
          onResizeEnd,
          getValueFromDOMRect: (rect: DOMRect) => rect.width,
        })
      );
    }
  }, [children, containerRef, onResizeEnd, setState]);

  useEffect(() => {
    if (isResizeEndScheduled) {
      // If reduceIsCollapsedAllChildren has updated the state in the above
      // useEffect, we have to call onResizeEnd in the following render update
      // when all the DOM rects have their sizes updated.
      if (onResizeEnd && containerRef.current) {
        onResizeEnd(getContainerState(containerRef.current).boxes);
      }
      setState(state => ({ ...state, isResizeEndScheduled: false }));
    }
  }, [containerRef, isResizeEndScheduled, onResizeEnd, setState]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mouseDownHandler = useCallback(
    getMouseDownHandler({
      containerRef,
      setState,
      getValueFromDOMRect: (rect: DOMRect) => rect.width,
      onResizeEnd,
      onResizeStart,
    }),
    [containerRef, setState]
  );

  const getSetCollapsed = useCallback(
    (index: number) => (isCollapsed: boolean) =>
      setState(state =>
        reduceIsCollapsed(
          updateConfigs(
            state,
            containerRef.current,
            (rect: DOMRect) => rect.width
          ),
          index,
          isCollapsed,
          false,
          onResizeEnd
        )
      ),
    [setState, onResizeEnd]
  );

  const isLast = (index: number) => index === children.length - 1;

  return (
    <HorizontalContainer onMouseDown={mouseDownHandler} ref={containerRef}>
      {children.flatMap(({ type: Section, props }, index) => [
        <HorizontalResizableSectionContainer
          key={`section-${index}`}
          style={styles[index]}
          $hasHorizontalScroll={hasHorizontalScroll}
        >
          <Section
            {...props}
            isCollapsed={configs[index].isCollapsed}
            setCollapsed={getSetCollapsed(index)}
          />
        </HorizontalResizableSectionContainer>,
        !isLast(index) && (
          <HorizontalResizer
            key={`resizer-${index}`}
            $isActive={index === activeResizerIndex}
            style={resizerStyles[index]}
          />
        ),
      ])}
    </HorizontalContainer>
  );
};

export { ResizableHorizontal };
